import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import * as styles from './Menu.css';
import { clsx } from 'clsx';
import { DEAL_TYPE_BY_CATEGORIES } from '../../constants/dealTypeByCategories';
import { useUpdateCategoryOnUserScroll } from './hooks/useUpdateCategoryOnUserScroll';
import { waitScrollEnd } from '../../utils/waitScrollEnd';
import { replaceHash } from '../../utils/replaceHash';
import { ExpandableList } from './components/ExpandableList/ExpandableList';
import { COMMON_CATEGORY_DATA } from '../../constants/commonCategoryData';

type TProps = { categoriesGroups: TGroup[] };

type TGroup = { dealType: string; name: string; categories: TCategory[] };

type TCategory = { category: string; name: string };

export const Menu = ({ categoriesGroups }: TProps) => {
  const [activeDealType, setActiveDealType] = useState<string | undefined>();
  const [activeCategory, setActiveCategory] = useState<string | undefined>();

  const selectCategory = useCallback((category: string) => {
    setActiveCategory(category);
    setActiveDealType(DEAL_TYPE_BY_CATEGORIES[category]);
  }, []);

  const autoScrollStartTimestamp = useRef<number | null>(null);

  const scrollToCategory = useCallback(async (category: string, smooth?: boolean) => {
    const timeStamp = Date.now();
    autoScrollStartTimestamp.current = timeStamp;
    document.getElementById(category)?.scrollIntoView({ block: 'start', behavior: smooth ? 'smooth' : 'auto' });
    await waitScrollEnd();
    if (timeStamp === autoScrollStartTimestamp.current) {
      autoScrollStartTimestamp.current = null;
    }
  }, []);

  const handleCategoryClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>, category: string) => {
      e.preventDefault();

      selectCategory(category);
      replaceHash(category);
      scrollToCategory(category, true);
    },
    [scrollToCategory, selectCategory],
  );

  const handleCategoryShowsInView = useCallback(
    (category: string) => {
      if (autoScrollStartTimestamp.current) {
        return;
      }
      selectCategory(category);
      replaceHash(category);
    },
    [selectCategory],
  );

  useEffect(() => {
    const category = window.location.hash.slice(1);
    selectCategory(category || COMMON_CATEGORY_DATA.category);
    if (category) {
      scrollToCategory(category);
    }
  }, [scrollToCategory, selectCategory]);

  useUpdateCategoryOnUserScroll(handleCategoryShowsInView);

  return (
    <ul className={styles['list']}>
      <li
        className={clsx(
          styles['category'],
          activeCategory === COMMON_CATEGORY_DATA.category && styles['category--active'],
        )}
      >
        <a
          className={styles['category-link']}
          href={`#${COMMON_CATEGORY_DATA.category}`}
          onClick={e => handleCategoryClick(e, COMMON_CATEGORY_DATA.category)}
        >
          {COMMON_CATEGORY_DATA.name}
        </a>
      </li>

      {categoriesGroups.map(({ dealType, name, categories }) => {
        const isGroupExpanded = activeDealType === dealType;

        return (
          <li key={dealType} className={clsx(styles['group'], isGroupExpanded && styles['group--active'])}>
            <span
              className={styles['group-title']}
              onClick={() => setActiveDealType(isGroupExpanded ? undefined : dealType)}
            >
              {name}
            </span>
            <ExpandableList isExpanded={isGroupExpanded}>
              {categories.map(({ category, name }) => {
                return (
                  <li
                    key={category}
                    className={clsx(styles['category'], activeCategory === category && styles['category--active'])}
                  >
                    <a
                      className={styles['category-link']}
                      href={`#${category}`}
                      onClick={e => handleCategoryClick(e, category)}
                    >
                      {name}
                    </a>
                  </li>
                );
              })}
            </ExpandableList>
          </li>
        );
      })}
    </ul>
  );
};
